/**
 * Centralised icon set.
 * -------------------------------------------------------------------------
 * Inline SVGs are preferred over an icon font here so that:
 *   - colour can be controlled per-instance via `currentColor`
 *   - the bundle stays free of font-loading flashes
 *   - icons can be tree-shaken by name
 *
 * If the set grows past ~20 glyphs, migrate to an SVG sprite. For now,
 * a simple component map keeps the call sites clean (<Icon name="news" />).
 *
 * All icons are designed on a 24×24 grid with 1.5 px stroke width — change
 * this together so the visual rhythm of the shell stays consistent.
 */

const paths = {
  news: (
    <>
      <path d="M4 7a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7z" />
      <path d="M18 8h1.5A1.5 1.5 0 0 1 21 9.5V18a3 3 0 0 1-3 3" />
      <path d="M8 10h6M8 13h6M8 16h4" />
    </>
  ),
  flag: (
    <>
      <path d="M5 21V4" />
      <path d="M5 4h11l-1.5 4L16 12H5" />
    </>
  ),
  spark: (
    <>
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  oracle: (
    <>
      <path d="M12 3a7 7 0 0 0-7 7c0 2.5 1.3 4.7 3.3 6L8 19l4-2 4 2-.3-3c2-1.3 3.3-3.5 3.3-6a7 7 0 0 0-7-7z" />
      <circle cx="9.5" cy="10" r="1" />
      <circle cx="14.5" cy="10" r="1" />
      <path d="M9.5 13.5c.6.6 1.5 1 2.5 1s1.9-.4 2.5-1" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </>
  ),
  chevronRight: <path d="m9 6 6 6-6 6" />,
  chevronLeft: <path d="m15 6-6 6 6 6" />,
  chevronDown: <path d="m6 9 6 6 6-6" />,
  chevronUp: <path d="m6 15 6-6 6 6" />,
  external: (
    <>
      <path d="M14 4h6v6" />
      <path d="M20 4 11 13" />
      <path d="M18 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h5" />
    </>
  ),
  send: (
    <>
      <path d="M3.5 12 21 4l-8 17.5-2.5-7L3.5 12z" />
    </>
  ),
  signal: (
    <>
      <rect x="3" y="14" width="3" height="6" rx="0.5" />
      <rect x="9" y="10" width="3" height="10" rx="0.5" />
      <rect x="15" y="6" width="3" height="14" rx="0.5" />
    </>
  ),
  wifi: (
    <>
      <path d="M2 8.5C4.5 6 8 4.5 12 4.5s7.5 1.5 10 4" />
      <path d="M5 12c2-1.7 4.4-2.7 7-2.7s5 1 7 2.7" />
      <path d="M8.5 15c1-.8 2.2-1.3 3.5-1.3s2.5.5 3.5 1.3" />
      <circle cx="12" cy="18" r="1" fill="currentColor" />
    </>
  ),
  battery: (
    <>
      <rect x="2" y="7" width="18" height="10" rx="2" />
      <path d="M22 11v2" />
      <rect x="4" y="9" width="13" height="6" rx="0.5" fill="currentColor" stroke="none" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  refresh: (
    <>
      <path d="M20 11A8 8 0 0 0 6.3 6.3L4 8.5" />
      <path d="M4 4v4.5H8.5" />
      <path d="M4 13a8 8 0 0 0 13.7 4.7L20 15.5" />
      <path d="M20 20v-4.5h-4.5" />
    </>
  ),
  bolt: <path d="M13 3 5 14h6l-1 7 8-11h-6l1-7z" />,
  trophy: (
    <>
      <path d="M8 3h8v4a4 4 0 0 1-8 0V3z" />
      <path d="M16 5h3a2 2 0 0 1 0 4h-3M8 5H5a2 2 0 0 0 0 4h3" />
      <path d="M12 11v4M9 18h6M10 15h4" />
    </>
  ),
  alert: (
    <>
      <path d="M12 3 2 20h20L12 3z" />
      <path d="M12 10v4M12 17h.01" />
    </>
  ),
  close: <path d="M6 6l12 12M18 6 6 18" />,
  filter: (
    <>
      <path d="M4 5h16M7 12h10M10 19h4" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </>
  ),
  settings: (
    <>
      <path d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" />
      <path d="M4 12h2M18 12h2M12 4v2M12 18v2M6.6 6.6l1.4 1.4M16 16l1.4 1.4M17.4 6.6 16 8M8 16l-1.4 1.4" />
    </>
  ),
};

export default function Icon({ name, size = 22, className = '', strokeWidth = 1.6, ...rest }) {
  const glyph = paths[name];
  if (!glyph) {
    if (typeof console !== 'undefined') {
      console.warn(`<Icon /> rendered unknown name: "${name}"`);
    }
    return null;
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      {...rest}
    >
      {glyph}
    </svg>
  );
}
